import React, { useState, useRef, useCallback, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import Navigation from "../components/Navigation";
import Footer from "../components/Footer";
import { 
  ChevronLeft, ChevronRight, Home, Bed, UtensilsCrossed, Sofa, Check, 
  Upload, QrCode, Camera, X, Download, Share2, RefreshCw, 
  ShoppingBag, LogOut, ZoomIn, ZoomOut, RotateCcw, Grid3X3, ChevronDown,
  Move, Undo2, GitCompare, Maximize2
} from "lucide-react";
import { products } from "../data/products";

const RoomVisualizer = () => {
  const navigate = useNavigate();
  const [selectedRoom, setSelectedRoom] = useState(0);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [uploadedImage, setUploadedImage] = useState(null);
  const [originalImage, setOriginalImage] = useState(null);
  const [showQRModal, setShowQRModal] = useState(false);
  const [isVisualizerMode, setIsVisualizerMode] = useState(false);
  const [floorOpacity, setFloorOpacity] = useState(85);
  const [showShareModal, setShowShareModal] = useState(false);
  const [shareSuccess, setShareSuccess] = useState(false);
  const [activeCategory, setActiveCategory] = useState("all");
  const [showProductDropdown, setShowProductDropdown] = useState(false);
  
  // New states for image manipulation
  const [zoom, setZoom] = useState(100);
  const [rotation, setRotation] = useState(0);
  const [angle, setAngle] = useState(0);
  const [showCompare, setShowCompare] = useState(false);
  const [comparePosition, setComparePosition] = useState(50);
  const [floorTextureKey, setFloorTextureKey] = useState(0);
  
  const fileInputRef = useRef(null);
  const visualizerRef = useRef(null);
  const compareRef = useRef(null);

  // Room presets
  const rooms = [
    {
      name: "Living Room",
      icon: Sofa,
      image: "https://images.unsplash.com/photo-1600210492486-724fe5c67fb0?w=1200&q=90",
      floorArea: { top: "55%", left: "0%", width: "100%", height: "45%" }
    },
    {
      name: "Bedroom",
      icon: Bed,
      image: "https://images.unsplash.com/photo-1616594039964-ae9021a400a0?w=1200&q=90",
      floorArea: { top: "60%", left: "0%", width: "100%", height: "40%" }
    },
    {
      name: "Kitchen",
      icon: UtensilsCrossed,
      image: "https://images.unsplash.com/photo-1556909114-f6e7ad7d3136?w=1200&q=90",
      floorArea: { top: "50%", left: "0%", width: "100%", height: "50%" }
    },
    {
      name: "Dining Room",
      icon: Home,
      image: "https://images.unsplash.com/photo-1617806118233-18e1de247200?w=1200&q=90",
      floorArea: { top: "55%", left: "0%", width: "100%", height: "45%" }
    }
  ];

  // Flooring categories
  const flooringCategories = [
    { id: "all", name: "All Products" },
    { id: "laminate", name: "ComfortSense (Laminate)" },
    { id: "vinyl", name: "SolidSense (Vinyl)" },
    { id: "hardwood", name: "NatureSense (Hardwood)" }
  ];

  // Backend URL for image proxy
  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

  // Get proxied image URL for peakstonefloor.ca images
  const getProxiedImageUrl = (originalUrl) => {
    if (!originalUrl) return null;
    // Use the backend proxy to bypass CORS
    return `${BACKEND_URL}/api/proxy-image?url=${encodeURIComponent(originalUrl)}`;
  };

  // Get floor texture URL for a product (using proxy)
  const getFloorTextureUrl = (product) => {
    if (!product || !product.image) return null;
    return getProxiedImageUrl(product.image);
  };

  // Get filtered products
  const getFilteredProducts = () => {
    const flooringProducts = products.filter(p => 
      p.collection === "ComfortSense" || 
      p.collection === "SolidSense" || 
      p.collection === "NatureSense"
    );

    if (activeCategory === "all") return flooringProducts;
    if (activeCategory === "laminate") return flooringProducts.filter(p => p.collection === "ComfortSense");
    if (activeCategory === "vinyl") return flooringProducts.filter(p => p.collection === "SolidSense");
    if (activeCategory === "hardwood") return flooringProducts.filter(p => p.collection === "NatureSense");
    return flooringProducts;
  };

  const filteredProducts = getFilteredProducts();
  const currentRoom = rooms[selectedRoom] || rooms[0];

  // Force re-render of floor texture when product changes
  useEffect(() => {
    if (selectedProduct) {
      setFloorTextureKey(prev => prev + 1);
    }
  }, [selectedProduct?.id]);

  // Handle file upload
  const handleFileUpload = (event) => {
    const file = event.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        const imageData = e.target.result;
        setUploadedImage(imageData);
        setOriginalImage(imageData);
        setIsVisualizerMode(true);
        // Reset transformations
        setZoom(100);
        setRotation(0);
        setAngle(0);
        if (!selectedProduct && filteredProducts.length > 0) {
          setSelectedProduct(filteredProducts[0]);
        }
      };
      reader.readAsDataURL(file);
    }
  };

  // Toolbar functions
  const handleRevert = () => {
    setZoom(100);
    setRotation(0);
    setAngle(0);
    if (originalImage) {
      setUploadedImage(originalImage);
    }
  };

  const handleZoomIn = () => {
    setZoom(prev => Math.min(prev + 10, 200));
  };

  const handleZoomOut = () => {
    setZoom(prev => Math.max(prev - 10, 50));
  };

  const handleRotate = () => {
    setRotation(prev => (prev + 90) % 360);
  };

  const handleAngle = () => {
    setAngle(prev => (prev + 5) % 20);
  };

  const handleCompare = () => {
    setShowCompare(!showCompare);
  };

  // Handle compare slider
  const handleCompareMove = (e) => {
    if (!compareRef.current) return;
    const rect = compareRef.current.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const percentage = (x / rect.width) * 100;
    setComparePosition(Math.max(0, Math.min(100, percentage)));
  };

  // Exit visualizer mode
  const handleExit = () => {
    setIsVisualizerMode(false);
    setUploadedImage(null);
    setOriginalImage(null);
    setSelectedProduct(null);
    setZoom(100);
    setRotation(0);
    setAngle(0);
    setShowCompare(false);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  // Change/re-upload image
  const handleChange = useCallback(() => {
    if (fileInputRef.current) {
      // Reset the file input value so the same file can be re-selected
      fileInputRef.current.value = '';
      // Trigger file picker
      fileInputRef.current.click();
    }
  }, []);

  // Download
  const handleDownload = useCallback(() => {
    const link = document.createElement('a');
    link.download = `timberlake-visualizer-${selectedProduct?.name || 'room'}-${Date.now()}.png`;
    
    // Create a simple download of the current view
    const img = uploadedImage || currentRoom.image;
    link.href = img;
    link.click();
  }, [uploadedImage, currentRoom, selectedProduct]);

  // Share
  const handleShare = () => {
    setShowShareModal(true);
  };

  const copyShareLink = () => {
    const shareText = `Check out this flooring visualization from Timberlake Flooring! ${selectedProduct ? `Product: ${selectedProduct.name} (${selectedProduct.collection})` : ''} - Visit timberlakeflooring.ca`;
    navigator.clipboard.writeText(shareText);
    setShareSuccess(true);
    setTimeout(() => setShareSuccess(false), 2000);
  };

  // Go to product
  const handleGoToProduct = () => {
    if (selectedProduct) {
      navigate(`/request-quote?product=${encodeURIComponent(selectedProduct.name)}`);
    } else {
      navigate('/products');
    }
  };

  // Select room preset
  const handleSelectRoom = (index) => {
    setSelectedRoom(index);
    setUploadedImage(null);
    setOriginalImage(null);
    setIsVisualizerMode(true);
    setZoom(100);
    setRotation(0);
    setAngle(0);
    if (!selectedProduct && filteredProducts.length > 0) {
      setSelectedProduct(filteredProducts[0]);
    }
  };

  // Select product
  const handleSelectProduct = (product) => {
    setSelectedProduct(product);
  };

  // Render upload UI
  const renderUploadUI = () => (
    <div className="max-w-7xl mx-auto px-4 py-8">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Upload Section */}
        <div className="bg-white rounded-2xl shadow-xl p-8">
          <h2 className="text-2xl font-bold text-luxury-navy mb-6 flex items-center gap-3">
            <Camera className="w-7 h-7 text-luxury-gold" />
            Upload Your Room
          </h2>
          <p className="text-gray-600 mb-6">
            Upload a photo of your room to see how our flooring will look in your space.
          </p>
          
          <div 
            className="border-3 border-dashed border-luxury-gold/50 rounded-xl p-8 text-center hover:border-luxury-gold hover:bg-luxury-gold/5 transition-all cursor-pointer"
            onClick={() => fileInputRef.current?.click()}
          >
            <Upload className="w-16 h-16 text-luxury-gold mx-auto mb-4" />
            <p className="text-lg font-semibold text-luxury-navy mb-2">
              Click to upload or drag and drop
            </p>
            <p className="text-sm text-gray-500">PNG, JPG, WEBP up to 10MB</p>
          </div>

          <div className="mt-6 flex gap-4">
            <button
              onClick={() => fileInputRef.current?.click()}
              className="flex-1 bg-luxury-navy text-white py-3 px-6 rounded-lg font-semibold hover:bg-gray-800 transition-all flex items-center justify-center gap-2"
            >
              <Upload className="w-5 h-5" />
              Upload Photo
            </button>
            <button
              onClick={() => setShowQRModal(true)}
              className="flex-1 bg-gray-100 text-luxury-navy py-3 px-6 rounded-lg font-semibold hover:bg-gray-200 transition-all flex items-center justify-center gap-2"
            >
              <QrCode className="w-5 h-5" />
              Scan QR Code
            </button>
          </div>
        </div>

        {/* Room Presets */}
        <div className="bg-white rounded-2xl shadow-xl p-8">
          <h2 className="text-2xl font-bold text-luxury-navy mb-6 flex items-center gap-3">
            <Home className="w-7 h-7 text-luxury-gold" />
            Or Choose a Room
          </h2>
          <p className="text-gray-600 mb-6">
            Select from our pre-designed room templates.
          </p>
          
          <div className="grid grid-cols-2 gap-4">
            {rooms.map((room, index) => {
              const Icon = room.icon;
              return (
                <button
                  key={index}
                  onClick={() => handleSelectRoom(index)}
                  className="relative group overflow-hidden rounded-xl border-2 border-gray-200 hover:border-luxury-gold transition-all"
                >
                  <img
                    src={room.image}
                    alt={room.name}
                    className="w-full h-32 object-cover group-hover:scale-110 transition-transform duration-500"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent" />
                  <div className="absolute bottom-0 left-0 right-0 p-3 flex items-center gap-2">
                    <Icon className="w-5 h-5 text-white" />
                    <span className="text-white font-medium">{room.name}</span>
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );

  // Render visualizer UI
  const renderVisualizerUI = () => (
    <div className="bg-gray-900 min-h-screen">
      {/* Top Toolbar */}
      <div className="bg-luxury-navy border-b border-gray-700 px-4 py-3">
        <div className="max-w-7xl mx-auto flex items-center justify-between flex-wrap gap-2">
          {/* Left Controls */}
          <div className="flex items-center gap-1 sm:gap-2">
            <button
              onClick={handleExit}
              className="flex items-center gap-1.5 text-white hover:text-luxury-gold transition-colors px-2 sm:px-3 py-2 rounded-lg hover:bg-white/10"
              title="Exit"
            >
              <LogOut className="w-5 h-5" />
              <span className="hidden sm:inline text-sm">Exit</span>
            </button>
            
            <div className="h-6 w-px bg-gray-600 hidden sm:block" />
            
            <button
              onClick={handleRevert}
              className="flex items-center gap-1.5 text-white hover:text-luxury-gold transition-colors px-2 sm:px-3 py-2 rounded-lg hover:bg-white/10"
              title="Revert to Original"
            >
              <Undo2 className="w-5 h-5" />
              <span className="hidden sm:inline text-sm">Revert</span>
            </button>
            
            <button
              onClick={handleChange}
              className="flex items-center gap-1.5 text-white hover:text-luxury-gold transition-colors px-2 sm:px-3 py-2 rounded-lg hover:bg-white/10"
              title="Change Photo"
            >
              <RefreshCw className="w-5 h-5" />
              <span className="hidden sm:inline text-sm">Change</span>
            </button>
          </div>

          {/* Center Controls - Image Manipulation */}
          <div className="flex items-center gap-1 bg-gray-800 rounded-lg px-2 py-1">
            <button
              onClick={handleAngle}
              className={`flex items-center gap-1.5 px-2 sm:px-3 py-1.5 rounded-md transition-colors ${angle > 0 ? 'bg-luxury-gold text-luxury-navy' : 'text-white hover:bg-gray-700'}`}
              title="Adjust Angle"
            >
              <Move className="w-4 h-4" />
              <span className="hidden md:inline text-sm">Angle</span>
            </button>
            
            <button
              onClick={handleZoomIn}
              className="flex items-center gap-1.5 text-white hover:bg-gray-700 px-2 sm:px-3 py-1.5 rounded-md transition-colors"
              title="Zoom In"
            >
              <ZoomIn className="w-4 h-4" />
              <span className="hidden md:inline text-sm">Zoom+</span>
            </button>
            
            <button
              onClick={handleZoomOut}
              className="flex items-center gap-1.5 text-white hover:bg-gray-700 px-2 sm:px-3 py-1.5 rounded-md transition-colors"
              title="Zoom Out"
            >
              <ZoomOut className="w-4 h-4" />
              <span className="hidden md:inline text-sm">Zoom-</span>
            </button>
            
            <button
              onClick={handleRotate}
              className={`flex items-center gap-1.5 px-2 sm:px-3 py-1.5 rounded-md transition-colors ${rotation > 0 ? 'bg-luxury-gold text-luxury-navy' : 'text-white hover:bg-gray-700'}`}
              title="Rotate"
            >
              <RotateCcw className="w-4 h-4" />
              <span className="hidden md:inline text-sm">Rotate</span>
            </button>
            
            <button
              onClick={handleCompare}
              className={`flex items-center gap-1.5 px-2 sm:px-3 py-1.5 rounded-md transition-colors ${showCompare ? 'bg-luxury-gold text-luxury-navy' : 'text-white hover:bg-gray-700'}`}
              title="Compare Before/After"
            >
              <GitCompare className="w-4 h-4" />
              <span className="hidden md:inline text-sm">Compare</span>
            </button>
          </div>

          {/* Right Controls */}
          <div className="flex items-center gap-1 sm:gap-2">
            <button
              onClick={handleShare}
              className="flex items-center gap-1.5 text-white hover:text-luxury-gold transition-colors px-2 sm:px-3 py-2 rounded-lg hover:bg-white/10"
              title="Share"
            >
              <Share2 className="w-5 h-5" />
              <span className="hidden sm:inline text-sm">Share</span>
            </button>
            
            <button
              onClick={handleDownload}
              className="flex items-center gap-1.5 text-white hover:text-luxury-gold transition-colors px-2 sm:px-3 py-2 rounded-lg hover:bg-white/10"
              title="Download"
            >
              <Download className="w-5 h-5" />
              <span className="hidden sm:inline text-sm">Download</span>
            </button>
            
            <div className="h-6 w-px bg-gray-600 hidden sm:block" />
            
            <button
              onClick={handleGoToProduct}
              className="flex items-center gap-1.5 bg-luxury-gold text-luxury-navy px-3 sm:px-4 py-2 rounded-lg font-semibold hover:bg-yellow-400 transition-all"
              title="Go to Product"
            >
              <ShoppingBag className="w-5 h-5" />
              <span className="hidden sm:inline text-sm">Go to Product</span>
            </button>
          </div>
        </div>
      </div>

      {/* Zoom/Rotation indicator */}
      {(zoom !== 100 || rotation !== 0 || angle !== 0) && (
        <div className="bg-gray-800 text-white text-center py-1 text-sm">
          {zoom !== 100 && <span className="mx-2">Zoom: {zoom}%</span>}
          {rotation !== 0 && <span className="mx-2">Rotation: {rotation}°</span>}
          {angle !== 0 && <span className="mx-2">Angle: {angle}°</span>}
        </div>
      )}

      <div className="flex flex-col lg:flex-row">
        {/* Product Sidebar */}
        <div className="lg:w-80 bg-gray-800 p-4 lg:min-h-[calc(100vh-120px)] overflow-y-auto">
          <h3 className="text-white font-semibold mb-4 flex items-center gap-2">
            <Grid3X3 className="w-5 h-5 text-luxury-gold" />
            Select Flooring
          </h3>

          {/* Category Filter */}
          <div className="mb-4 relative">
            <button
              onClick={() => setShowProductDropdown(!showProductDropdown)}
              className="w-full bg-gray-700 text-white px-4 py-3 rounded-lg flex items-center justify-between hover:bg-gray-600 transition-colors"
            >
              <span>{flooringCategories.find(c => c.id === activeCategory)?.name}</span>
              <ChevronDown className={`w-5 h-5 transition-transform ${showProductDropdown ? 'rotate-180' : ''}`} />
            </button>
            {showProductDropdown && (
              <div className="absolute top-full left-0 right-0 mt-1 bg-gray-700 rounded-lg overflow-hidden z-20 shadow-xl">
                {flooringCategories.map((category) => (
                  <button
                    key={category.id}
                    onClick={() => {
                      setActiveCategory(category.id);
                      setShowProductDropdown(false);
                    }}
                    className={`w-full px-4 py-3 text-left hover:bg-gray-600 transition-colors ${
                      activeCategory === category.id ? 'bg-luxury-gold text-luxury-navy' : 'text-white'
                    }`}
                  >
                    {category.name}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Product Grid */}
          <div className="grid grid-cols-2 gap-3 max-h-[50vh] overflow-y-auto pr-2">
            {filteredProducts.map((product) => (
              <button
                key={product.id}
                onClick={() => handleSelectProduct(product)}
                className={`relative rounded-lg overflow-hidden border-2 transition-all ${
                  selectedProduct?.id === product.id
                    ? 'border-luxury-gold ring-2 ring-luxury-gold'
                    : 'border-transparent hover:border-gray-500'
                }`}
              >
                <img 
                  src={getProxiedImageUrl(product.image)}
                  alt={product.name}
                  className="w-full h-16 object-cover"
                  onError={(e) => {
                    e.target.style.backgroundColor = '#8B7355';
                    e.target.style.minHeight = '64px';
                  }}
                />
                <div className="bg-gray-700 p-1.5">
                  <p className="text-white text-xs font-medium truncate">{product.name}</p>
                  <p className="text-gray-400 text-xs truncate">{product.collection}</p>
                </div>
                {selectedProduct?.id === product.id && (
                  <div className="absolute top-1 right-1 bg-luxury-gold rounded-full p-0.5">
                    <Check className="w-3 h-3 text-luxury-navy" />
                  </div>
                )}
              </button>
            ))}
          </div>

          {/* Opacity Control */}
          <div className="mt-4 p-3 bg-gray-700 rounded-lg">
            <label className="text-white text-sm font-medium mb-2 block">
              Floor Intensity: {floorOpacity}%
            </label>
            <input
              type="range"
              min="50"
              max="100"
              value={floorOpacity}
              onChange={(e) => setFloorOpacity(Number(e.target.value))}
              className="w-full accent-luxury-gold"
            />
          </div>

          {/* Selected Product Info */}
          {selectedProduct && (
            <div className="mt-4 p-3 bg-luxury-gold/20 rounded-lg border border-luxury-gold/30">
              <div className="flex gap-3">
                <img
                  src={getProxiedImageUrl(selectedProduct.image)}
                  alt={selectedProduct.name}
                  className="w-16 h-16 rounded-lg object-cover"
                />
                <div className="flex-1">
                  <h4 className="text-luxury-gold font-semibold text-sm">{selectedProduct.name}</h4>
                  <p className="text-gray-300 text-xs">{selectedProduct.collection}</p>
                  <p className="text-gray-400 text-xs">{selectedProduct.category}</p>
                </div>
              </div>
              <button
                onClick={handleGoToProduct}
                className="mt-2 w-full bg-luxury-gold text-luxury-navy py-2 rounded-lg font-semibold hover:bg-yellow-400 transition-all text-sm"
              >
                Request Quote
              </button>
            </div>
          )}
        </div>

        {/* Main Visualizer Area */}
        <div className="flex-1 p-4 lg:p-6" ref={visualizerRef}>
          <div 
            className="relative rounded-xl overflow-hidden shadow-2xl max-w-4xl mx-auto bg-gray-800"
            ref={compareRef}
            onMouseMove={showCompare ? handleCompareMove : undefined}
          >
            {/* Room Navigation (for presets) */}
            {!uploadedImage && (
              <div className="absolute top-4 left-4 right-4 z-20 flex items-center justify-between">
                <button
                  onClick={() => setSelectedRoom((prev) => (prev - 1 + rooms.length) % rooms.length)}
                  className="p-2 rounded-full bg-black/50 text-white hover:bg-luxury-gold hover:text-luxury-navy transition-all"
                >
                  <ChevronLeft className="w-6 h-6" />
                </button>
                <div className="bg-black/50 px-4 py-2 rounded-full">
                  <span className="text-white font-medium">{currentRoom.name}</span>
                </div>
                <button
                  onClick={() => setSelectedRoom((prev) => (prev + 1) % rooms.length)}
                  className="p-2 rounded-full bg-black/50 text-white hover:bg-luxury-gold hover:text-luxury-navy transition-all"
                >
                  <ChevronRight className="w-6 h-6" />
                </button>
              </div>
            )}

            {/* Main Image Container */}
            <div 
              className="relative overflow-hidden"
              style={{
                transform: `scale(${zoom / 100}) rotate(${rotation}deg) perspective(1000px) rotateX(${angle}deg)`,
                transformOrigin: 'center center',
                transition: 'transform 0.3s ease-out',
              }}
            >
              {/* Room Image */}
              <img
                src={uploadedImage || currentRoom.image}
                alt={uploadedImage ? "Your Room" : currentRoom.name}
                className="w-full h-auto"
                crossOrigin="anonymous"
              />
              
              {/* Floor Overlay with Actual Product Texture */}
              {selectedProduct && (
                <div
                  key={`floor-${floorTextureKey}-${selectedProduct.id}`}
                  className="absolute overflow-hidden"
                  style={{
                    top: uploadedImage ? "50%" : currentRoom.floorArea.top,
                    left: "0%",
                    width: "100%",
                    height: uploadedImage ? "50%" : currentRoom.floorArea.height,
                    clipPath: showCompare ? `inset(0 ${100 - comparePosition}% 0 0)` : 'none',
                  }}
                >
                  {/* Full coverage product texture image */}
                  <img
                    src={getFloorTextureUrl(selectedProduct)}
                    alt={selectedProduct.name}
                    className="absolute inset-0 w-full h-full"
                    style={{
                      objectFit: 'cover',
                      opacity: floorOpacity / 100,
                    }}
                  />
                  {/* Depth gradient overlay */}
                  <div
                    className="absolute inset-0 pointer-events-none"
                    style={{
                      background: 'linear-gradient(to bottom, rgba(0,0,0,0) 0%, rgba(0,0,0,0.3) 100%)',
                    }}
                  />
                </div>
              )}

              {/* Compare mode - Original half */}
              {showCompare && (
                <div
                  className="absolute inset-0 overflow-hidden pointer-events-none"
                  style={{
                    clipPath: `inset(0 0 0 ${comparePosition}%)`,
                  }}
                >
                  <img
                    src={uploadedImage || currentRoom.image}
                    alt="Original"
                    className="w-full h-auto"
                  />
                  <div className="absolute top-4 right-4 bg-black/70 text-white px-3 py-1 rounded-full text-sm">
                    Original
                  </div>
                </div>
              )}

              {/* Compare slider line */}
              {showCompare && (
                <div
                  className="absolute top-0 bottom-0 w-1 bg-luxury-gold cursor-ew-resize z-30"
                  style={{ left: `${comparePosition}%` }}
                >
                  <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-10 h-10 bg-luxury-gold rounded-full flex items-center justify-center shadow-lg">
                    <ChevronLeft className="w-4 h-4 text-luxury-navy" />
                    <ChevronRight className="w-4 h-4 text-luxury-navy" />
                  </div>
                </div>
              )}
            </div>

            {/* Compare mode labels */}
            {showCompare && (
              <>
                <div className="absolute top-4 left-4 bg-luxury-gold text-luxury-navy px-3 py-1 rounded-full text-sm font-semibold z-20">
                  With Flooring
                </div>
              </>
            )}

            {/* Room Thumbnails */}
            {!uploadedImage && (
              <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 flex gap-2 z-20">
                {rooms.map((room, index) => (
                  <button
                    key={index}
                    onClick={() => setSelectedRoom(index)}
                    className={`w-14 h-10 rounded-lg overflow-hidden border-2 transition-all ${
                      selectedRoom === index ? 'border-luxury-gold' : 'border-white/30'
                    }`}
                  >
                    <img src={room.image} alt={room.name} className="w-full h-full object-cover" />
                  </button>
                ))}
              </div>
            )}

            {/* Product Preview Badge */}
            {selectedProduct && !showCompare && (
              <div className="absolute bottom-4 left-4 bg-black/80 backdrop-blur-sm rounded-lg p-2 flex items-center gap-2 z-20">
                <img
                  src={getProxiedImageUrl(selectedProduct.image)}
                  alt={selectedProduct.name}
                  className="w-12 h-12 rounded-md object-cover border border-luxury-gold"
                />
                <div>
                  <p className="text-white font-semibold text-sm">{selectedProduct.name}</p>
                  <p className="text-gray-300 text-xs">{selectedProduct.collection}</p>
                </div>
              </div>
            )}
          </div>

          {/* Bottom Info Bar */}
          <div className="max-w-4xl mx-auto mt-4 p-4 bg-gray-800 rounded-xl flex flex-wrap items-center justify-between gap-4">
            <div>
              <p className="text-luxury-gold text-sm">Currently Viewing</p>
              <p className="text-white font-semibold">
                {selectedProduct ? `${selectedProduct.name} - ${selectedProduct.collection}` : 'Select a flooring product'}
              </p>
            </div>
            <div className="flex gap-3">
              <button
                onClick={handleChange}
                className="px-4 py-2 bg-gray-700 text-white rounded-lg hover:bg-gray-600 transition-colors text-sm"
              >
                Change Photo
              </button>
              <button
                onClick={handleGoToProduct}
                className="px-4 py-2 bg-luxury-gold text-luxury-navy rounded-lg font-semibold hover:bg-yellow-400 transition-colors text-sm"
              >
                Get Quote
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-100">
      {!isVisualizerMode && <Navigation />}
      
      {!isVisualizerMode && (
        <>
          {/* Header */}
          <div className="bg-luxury-navy text-white py-12">
            <div className="max-w-7xl mx-auto px-4 text-center">
              <h1 className="text-4xl md:text-5xl font-bold mb-4 luxury-heading">
                Room Visualizer
              </h1>
              <p className="text-lg text-gray-300 max-w-2xl mx-auto">
                See how our premium flooring collections will transform your space.
              </p>
            </div>
          </div>

          {renderUploadUI()}

          {/* How It Works */}
          <div className="max-w-7xl mx-auto px-4 py-12">
            <div className="bg-white rounded-2xl shadow-xl p-8">
              <h2 className="text-3xl font-bold text-luxury-navy mb-8 text-center">
                How to Use the Room Visualizer
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
                {[
                  { num: "1", title: "Upload or Select", desc: "Upload your room photo or choose a preset" },
                  { num: "2", title: "Browse Products", desc: "Filter and select from our flooring collection" },
                  { num: "3", title: "Adjust & Compare", desc: "Use tools to zoom, rotate, and compare" },
                  { num: "4", title: "Share & Order", desc: "Download, share, or request a quote" }
                ].map((step, i) => (
                  <div key={i} className="text-center">
                    <div className="w-16 h-16 bg-luxury-gold rounded-full flex items-center justify-center mx-auto mb-4">
                      <span className="text-2xl font-bold text-luxury-navy">{step.num}</span>
                    </div>
                    <h3 className="font-semibold text-luxury-navy text-lg mb-2">{step.title}</h3>
                    <p className="text-gray-600 text-sm">{step.desc}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* CTA */}
          <div className="max-w-7xl mx-auto px-4 pb-12">
            <div className="bg-gradient-to-r from-luxury-navy to-gray-800 rounded-xl p-8 text-center">
              <h2 className="text-3xl font-bold text-white mb-4">
                Ready to Transform Your Space?
              </h2>
              <p className="text-gray-300 mb-6 max-w-2xl mx-auto">
                Contact us today for a free consultation and quote.
              </p>
              <div className="flex flex-wrap justify-center gap-4">
                <Link
                  to="/request-quote"
                  className="bg-luxury-gold text-luxury-navy px-8 py-3 rounded-lg font-semibold hover:bg-yellow-400 transition-all"
                >
                  Get Free Quote
                </Link>
                <Link
                  to="/contact"
                  className="bg-white/10 text-white border border-white px-8 py-3 rounded-lg font-semibold hover:bg-white hover:text-luxury-navy transition-all"
                >
                  Contact Us
                </Link>
              </div>
            </div>
          </div>

          <Footer />
        </>
      )}

      {isVisualizerMode && renderVisualizerUI()}

      {/* QR Modal */}
      {showQRModal && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-8 relative">
            <button
              onClick={() => setShowQRModal(false)}
              className="absolute top-4 right-4 p-2 hover:bg-gray-100 rounded-full"
            >
              <X className="w-6 h-6 text-gray-500" />
            </button>
            
            <div className="text-center">
              <div className="w-16 h-16 bg-luxury-gold/20 rounded-full flex items-center justify-center mx-auto mb-4">
                <QrCode className="w-8 h-8 text-luxury-navy" />
              </div>
              <h3 className="text-2xl font-bold text-luxury-navy mb-2">Scan QR Code</h3>
              <p className="text-gray-600 mb-6">
                Scan with your phone to upload a photo from your mobile device.
              </p>
              
              <div className="bg-white border-4 border-luxury-navy rounded-xl p-4 inline-block mb-6">
                <div className="w-48 h-48 bg-gray-100 flex items-center justify-center rounded-lg">
                  <svg viewBox="0 0 100 100" className="w-40 h-40">
                    <rect x="10" y="10" width="25" height="25" fill="#0a1628"/>
                    <rect x="65" y="10" width="25" height="25" fill="#0a1628"/>
                    <rect x="10" y="65" width="25" height="25" fill="#0a1628"/>
                    <rect x="15" y="15" width="15" height="15" fill="white"/>
                    <rect x="70" y="15" width="15" height="15" fill="white"/>
                    <rect x="15" y="70" width="15" height="15" fill="white"/>
                    <rect x="18" y="18" width="9" height="9" fill="#0a1628"/>
                    <rect x="73" y="18" width="9" height="9" fill="#0a1628"/>
                    <rect x="18" y="73" width="9" height="9" fill="#0a1628"/>
                    <rect x="40" y="40" width="20" height="20" fill="#0a1628"/>
                    <rect x="45" y="45" width="10" height="10" fill="white"/>
                    <rect x="48" y="48" width="4" height="4" fill="#d4af37"/>
                  </svg>
                </div>
              </div>
              
              <div className="flex gap-3">
                <button
                  onClick={() => {
                    setShowQRModal(false);
                    fileInputRef.current?.click();
                  }}
                  className="flex-1 bg-luxury-navy text-white py-3 rounded-lg font-semibold hover:bg-gray-800 flex items-center justify-center gap-2"
                >
                  <Upload className="w-5 h-5" />
                  Upload Instead
                </button>
                <button
                  onClick={() => setShowQRModal(false)}
                  className="flex-1 bg-gray-100 text-gray-700 py-3 rounded-lg font-semibold hover:bg-gray-200"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Share Modal */}
      {showShareModal && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-8 relative">
            <button
              onClick={() => setShowShareModal(false)}
              className="absolute top-4 right-4 p-2 hover:bg-gray-100 rounded-full"
            >
              <X className="w-6 h-6 text-gray-500" />
            </button>
            
            <div className="text-center">
              <div className="w-16 h-16 bg-luxury-gold/20 rounded-full flex items-center justify-center mx-auto mb-4">
                <Share2 className="w-8 h-8 text-luxury-navy" />
              </div>
              <h3 className="text-2xl font-bold text-luxury-navy mb-2">Share Visualization</h3>
              
              {selectedProduct && (
                <div className="bg-gray-50 rounded-lg p-4 mb-6 text-left">
                  <p className="text-sm text-gray-500">Selected Product:</p>
                  <p className="font-semibold text-luxury-navy">{selectedProduct.name}</p>
                  <p className="text-sm text-gray-600">{selectedProduct.collection}</p>
                </div>
              )}
              
              <div className="flex gap-3">
                <button
                  onClick={copyShareLink}
                  className="flex-1 bg-luxury-navy text-white py-3 rounded-lg font-semibold hover:bg-gray-800 flex items-center justify-center gap-2"
                >
                  {shareSuccess ? <Check className="w-5 h-5" /> : <Share2 className="w-5 h-5" />}
                  {shareSuccess ? 'Copied!' : 'Copy Share Text'}
                </button>
                <button
                  onClick={handleDownload}
                  className="flex-1 bg-luxury-gold text-luxury-navy py-3 rounded-lg font-semibold hover:bg-yellow-400 flex items-center justify-center gap-2"
                >
                  <Download className="w-5 h-5" />
                  Download
                </button>
              </div>
              
              <button
                onClick={() => setShowShareModal(false)}
                className="w-full mt-4 bg-gray-100 text-gray-700 py-3 rounded-lg font-semibold hover:bg-gray-200"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      <input
        type="file"
        ref={fileInputRef}
        onChange={handleFileUpload}
        accept="image/*"
        className="hidden"
      />
    </div>
  );
};

export default RoomVisualizer;
